import React, { useCallback, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Page as PageLayout, Button, useToast, DocsAnnotation } from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useLiveWorkflowTestExecutions } from '../../../features/workflows/useLiveWorkflowTestExecutions';
import { normalizeFromStream } from '../../../features/workflows/useRunSummaryData';
import { useWorkflowTestExecutions } from '../../../features/workflows/useWorkflowTestExecutions';
import { useTestRunsTrend } from '../../../features/workflows/useTestRunsTrend';
import {
  useCancelTestExecution,
  useDeleteTestExecution,
} from '../../../features/workflows/useAgenticRunDetail';
import { useHasEligibleWorkflows, useEligibleWorkflowSummaries } from '../../../hooks/useWorkflows';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';
import { startWorkflowTestRun } from '../../../features/workflows/workflowTestRunsApi';
import { StartTestRunDialog } from '../../components/workflows/StartTestRunDialog';
import { useRunTestSuite, useRunnableExternalTestSuites } from '../../../features/test-suites';
import {
  describeExternalCancel,
  useCancelExternalRun,
  useRerunExternalRun,
} from '../../../features/external-tests/useExternalRunActions';
import { TestRunConfirmationDialogs } from './test-runs/TestRunConfirmationDialogs';
import { TestRunsList } from './test-runs/TestRunsList';
import { useTestRunColumns } from './test-runs/useTestRunColumns';
import { TestRunsTrendChart } from './test-runs/TestRunsTrendChart';
import { TestRunsFilterBar } from './test-runs/TestRunsFilterBar';
import { RunsRail } from './test-runs/RunsRail';
import { TestRunsFilters } from './test-runs/TestRunsFilters';
import { buildTestRunsFilters, useTestRunsFilterState } from './test-runs/useTestRunsFilterState';
import type { TestRunsSearch } from './runsRouteSearch';
import type { RunsListRow } from '../../../features/workflows/useWorkflowTestExecutions';

import { ApiError } from '../../../lib/api-error';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import { toInclusiveIsoRange } from '../../../utils/dateRange';

const TREND_WINDOW_DAYS = 30;

export function TestRunsPage() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentOrgId = user?.orgId ?? null;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const searchParams = useSearch({ from: '/runs/test' }) as TestRunsSearch;
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const cancelMutation = useCancelTestExecution();
  const deleteMutation = useDeleteTestExecution();
  const cancelExternalRun = useCancelExternalRun();
  const rerunExternalRun = useRerunExternalRun();

  // Query params derived from the URL search state — needed here (ahead of
  // `useTestRunsFilterState`) because the trend/runs-list queries below need
  // them to fetch, and that hook's option-harvesting effects need those
  // queries' results as input. See `useTestRunsFilterState.ts`.
  const testRunsFilters = React.useMemo(() => buildTestRunsFilters(searchParams), [searchParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      navigate({
        to: '/runs/test',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page };
          if (page === 1) delete next.page;
          return next;
        },
      });
    },
    [navigate]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      navigate({
        to: '/runs/test',
        search: (prev: Record<string, unknown>) => ({ ...prev, page: 1, pageSize }),
      });
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => {
      navigate({
        to: '/runs/test',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page: 1, sortBy, sortOrder };
          if (!sortBy) {
            delete next.sortBy;
            delete next.sortOrder;
          }
          return next;
        },
      });
    },
    [navigate]
  );

  useBreadcrumbs([
    { key: 'suites', label: 'Suites', href: '/runs/test' },
    { key: 'test', label: 'Test Runs' },
  ]);

  const liveExecutions = useLiveWorkflowTestExecutions(currentOrgId);
  const { hasEligibleWorkflows, isLoading: isLoadingWorkflows } = useHasEligibleWorkflows();
  const runnableExternalSuitesQuery = useRunnableExternalTestSuites(
    Boolean(currentOrgId) && Boolean(user?.features?.externalTestSuites)
  );
  const runnableExternalSuites = runnableExternalSuitesQuery.data ?? [];
  // Per-org model split: flag ON → "eligible" wording, flag OFF (default) → "promoted".
  const runnableNoun = useEligibilityModel() ? 'eligible' : 'promoted';
  const workflowTestExecutionsQuery = useWorkflowTestExecutions(currentOrgId, {
    page: searchParams.page,
    pageSize: searchParams.pageSize,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    filters: testRunsFilters,
  });

  const testRunsTrendQuery = useTestRunsTrend(currentOrgId, {
    days: TREND_WINDOW_DAYS,
    filters: testRunsFilters,
  });

  const {
    targetFilter,
    statusFilter,
    triggeredByFilter,
    dateRange,
    updateSearch,
    targetOptions,
    triggeredByOptions,
    targetLabelsRef,
    triggeredByLabelsRef,
  } = useTestRunsFilterState({
    trendPoints: testRunsTrendQuery.data?.data,
    executions: workflowTestExecutionsQuery.data?.data,
  });

  // Fetch runnable workflow summaries for the run preview (lightweight: id + name only).
  // The endpoint returns the org's suite-runnable set per its model (eligible or promoted).
  const runnableSummariesQuery = useEligibleWorkflowSummaries(
    Boolean(currentOrgId) && (liveExecutions.runs.length > 0 || dialogOpen),
    { withTags: dialogOpen }
  );
  const runnableWorkflows = runnableSummariesQuery.data?.workflows ?? [];

  const selectedRun =
    liveExecutions.runs.find((r) => r.suiteId === liveExecutions.selectedRunId) ??
    liveExecutions.runs[0];
  const liveRunSummary = React.useMemo(() => {
    if (!selectedRun) return null;
    const testKeys = Object.keys(selectedRun.executionState.tests);
    // When tests are populated (after hydration), restrict to only those workflows;
    // otherwise fall back to all eligible (covers initial load before hydration)
    const relevantWorkflows =
      testKeys.length > 0
        ? runnableWorkflows.filter((w) => testKeys.includes(w.id))
        : runnableWorkflows;
    return normalizeFromStream(
      selectedRun.suiteId,
      selectedRun.executionStatus,
      selectedRun.executionDurationMs,
      selectedRun.executionState,
      relevantWorkflows
    );
  }, [selectedRun, runnableWorkflows]);

  const startAgenticRunMutation = useMutation({
    mutationFn: startWorkflowTestRun,
    meta: { suppressToast: true },
    onSuccess: (data) => {
      toast.success('Test run started');
      if (data.suiteId) {
        liveExecutions.addRun(data.suiteId, data.startedAt ?? null);
        // `addRun` inserts the run optimistically with no run target — the
        // response doesn't carry one. Only discovery resolves it, so without
        // this the run the user just started keeps a spinner and a generic
        // "Test run" label for its whole life, which is the exact thing the
        // named-run work exists to prevent.
        liveExecutions.refresh();
      }
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions', currentOrgId] });
      // A run started with "Re-enable degraded flows" clears health on those flows,
      // so the cached summaries and health badges are stale the moment it starts.
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start test run', {
        reportError: !(error instanceof ApiError && error.userError),
      });
    },
  });

  const runSuite = useRunTestSuite();
  const runSuiteMutation = useMutation({
    mutationFn: async (params: {
      suiteId: string;
      environmentMode?: 'mixed' | 'force';
      reEnableDegraded?: boolean;
    }) => ({
      suiteId: params.suiteId,
      result: await runSuite.mutateAsync({
        id: params.suiteId,
        environmentMode: params.environmentMode,
        reEnableDegraded: params.reEnableDegraded,
      }),
    }),
    onSuccess: ({ suiteId, result }) => {
      toast.success('Suite run started');
      // An external suite has no `executionId` at all — it lands on its own
      // detail route, which is why the response is discriminated.
      if (result.kind === 'external') {
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
        return;
      }
      navigate({
        to: '/suites/$suiteId/runs/$executionId',
        params: { suiteId, executionId: result.executionId },
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to run suite');
    },
  });

  const handleNewTestRun = () => {
    if (!hasEligibleWorkflows && runnableExternalSuites.length === 0) {
      toast.info(
        `No ${runnableNoun} workflows. Approve and pass a workflow first to run it in suites.`
      );
      return;
    }
    if (!hasEligibleWorkflows && runnableExternalSuites.length === 1) {
      runSuiteMutation.mutate({ suiteId: runnableExternalSuites[0].id });
      return;
    }
    setDialogOpen(true);
  };

  const newRunButton = (size: 'sm' | 'md') => (
    <Button
      size={size}
      variant="primary"
      iconLeft={<Play size={size === 'sm' ? 14 : 16} weight="fill" />}
      isDisabled={
        startAgenticRunMutation.isPending ||
        runSuiteMutation.isPending ||
        isLoadingWorkflows ||
        runnableExternalSuitesQuery.isLoading ||
        (!hasEligibleWorkflows && runnableExternalSuites.length === 0)
      }
      onPress={handleNewTestRun}
    >
      {!hasEligibleWorkflows &&
      runnableExternalSuites.length === 0 &&
      !isLoadingWorkflows &&
      !runnableExternalSuitesQuery.isLoading
        ? `New Test Run (no ${runnableNoun} flows)`
        : 'New Test Run'}
    </Button>
  );

  const handleRequestCancel = useCallback((executionId: string) => {
    setCancelTargetId(executionId);
  }, []);

  const handleRequestDelete = useCallback((executionId: string) => {
    setDeleteTargetId(executionId);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTargetId) return;
    try {
      await cancelMutation.mutateAsync(cancelTargetId);
      toast.success('Test run canceled');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel');
    }
    setCancelTargetId(null);
  }, [cancelMutation, cancelTargetId, toast]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      toast.success('Test run deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
    setDeleteTargetId(null);
  }, [deleteMutation, deleteTargetId, toast]);

  // External cancel/rerun act immediately rather than through the confirmation
  // dialogs above: those dialogs are worded for `workflow_test_executions` and
  // hold a single target id, and re-running is not destructive to anything —
  // it opens a new run row and leaves the old one exactly as it was.
  const handleExternalCancel = useCallback(
    async (runId: string) => {
      try {
        const result = await cancelExternalRun.mutateAsync(runId);
        toast.success(describeExternalCancel(result));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel this run');
      }
    },
    [cancelExternalRun, toast]
  );

  const handleExternalRerun = useCallback(
    async (runId: string) => {
      try {
        const result = await rerunExternalRun.mutateAsync({ runId });
        toast.success('Run started');
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to re-run this suite');
      }
    },
    [navigate, rerunExternalRun, toast]
  );

  const testRunColumns = useTestRunColumns({
    isCancelPending: cancelMutation.isPending,
    isDeletePending: deleteMutation.isPending,
    onCancel: handleRequestCancel,
    onDelete: handleRequestDelete,
    isExternalCancelPending: cancelExternalRun.isPending,
    isExternalRerunPending: rerunExternalRun.isPending,
    onExternalCancel: (runId) => void handleExternalCancel(runId),
    onExternalRerun: (runId) => void handleExternalRerun(runId),
  });

  const handleDateRangeChange = useCallback(
    (range: Parameters<typeof toInclusiveIsoRange>[0]) => {
      const iso = toInclusiveIsoRange(range);
      updateSearch({ startedAfter: iso.start, startedBefore: iso.end, page: 1 });
    },
    [updateSearch]
  );

  const handleClearDateRange = useCallback(() => {
    updateSearch({ startedAfter: undefined, startedBefore: undefined, page: 1 });
  }, [updateSearch]);

  const {
    filterDialogTrigger,
    activeFilterChips,
    hasActiveFilters,
    handleFilterRemove,
    handleResetFilters,
  } = TestRunsFilters({
    targetFilter,
    statusFilter,
    triggeredByFilter,
    dateRange,
    targetLabelMap: Object.fromEntries(targetLabelsRef.current),
    triggeredByLabelMap: Object.fromEntries(triggeredByLabelsRef.current),
    onFilterChange: updateSearch,
    onDateRangeChange: handleDateRangeChange,
    onClearDateRange: handleClearDateRange,
    isOpen: isFilterDialogOpen,
    onOpenChange: setIsFilterDialogOpen,
  });

  // The filter bar, sparkline strip, and table fuse into one bordered unit
  // only once the table has real rows to show (mirrors `CurrentIssuesPage`'s
  // `isTableLockup`) — a loading/empty table falls back to its own EmptyState
  // with no border to fuse against, so forcing square corners there would
  // just leave a stray line above empty space.
  const isTableLockup =
    !workflowTestExecutionsQuery.isLoading &&
    Boolean(workflowTestExecutionsQuery.data?.data?.length) &&
    Boolean(workflowTestExecutionsQuery.data?.pagination);

  return (
    <PageLayout>
      <DocsAnnotation slug="reference/test-runs" title="Test Runs">
        {/* No page title here — the breadcrumb above already reads "Suites ›
            Test Runs". Mirrors the sibling `runs/*Page.tsx` idiom of a bare
            right-aligned action row (see e.g. `QaRunsPage.tsx`) in place of a
            `PageHeader` when there's nothing else for the header to say. */}
        <div className="flex justify-end mb-4">{newRunButton('md')}</div>

        {/* Rail is DOM-first so it's on top below `xl` (flex-col, default
            document order) and on the right at `xl` and up
            (`xl:flex-row-reverse` places the first child at the reversed
            main-start, i.e. the right edge). It renders nothing of its own
            when there's neither a live run nor anything upcoming, so the
            lockup takes the full width instead of leaving an empty column. */}
        <div className="flex flex-col xl:flex-row-reverse gap-4 items-start">
          <RunsRail
            liveRuns={liveExecutions.runs}
            selectedRun={selectedRun ?? null}
            onRunSelect={liveExecutions.setSelectedRunId}
            liveData={liveRunSummary}
            connectionState={selectedRun?.connectionState}
            onRetry={selectedRun?.retryConnection}
            onItemSelect={(item) =>
              item.workflowRunId
                ? navigate({
                    to: '/runs/$runId',
                    params: { runId: item.workflowRunId },
                    search: { testRunId: item.id },
                  })
                : navigate({ to: '/runs/tests/$testRunId', params: { testRunId: item.id } })
            }
            onUpcomingRunTriggered={liveExecutions.refresh}
          />

          <div className="min-w-0 flex-1 w-full">
            <TestRunsFilterBar
              targetFilter={targetFilter}
              targetOptions={targetOptions}
              statusFilter={statusFilter}
              triggeredByFilter={triggeredByFilter}
              triggeredByOptions={triggeredByOptions}
              onFilterChange={updateSearch}
              filterDialogTrigger={filterDialogTrigger}
              activeFilterChips={activeFilterChips}
              onChipRemove={handleFilterRemove}
              onClearAll={handleResetFilters}
              showClearAll={hasActiveFilters}
              attached={isTableLockup}
            />

            <TestRunsTrendChart
              className={isTableLockup ? undefined : 'mt-4'}
              query={testRunsTrendQuery}
              attached={isTableLockup}
              hasActiveFilters={hasActiveFilters}
              windowDays={TREND_WINDOW_DAYS}
            />

            <div className={isTableLockup ? undefined : 'mt-4'}>
              <TestRunsList
                columns={testRunColumns}
                executions={workflowTestExecutionsQuery.data?.data}
                hasEligibleWorkflows={hasEligibleWorkflows}
                hasRunnableExternalSuites={runnableExternalSuites.length > 0}
                isLoading={workflowTestExecutionsQuery.isLoading}
                pagination={workflowTestExecutionsQuery.data?.pagination}
                runnableNoun={runnableNoun}
                searchParams={searchParams}
                selectedRunId={liveExecutions.selectedRunId}
                containerClassName={
                  isTableLockup ? 'rounded-t-none border-t-0 filter-lockup-table' : undefined
                }
                buildRunHref={(execution) => {
                  // Exhaustive switch on `kind` — never an `else`/ternary
                  // fallthrough. Native rows carry `kind?: 'cofactor'` (optional,
                  // for back-compat), so both the `'cofactor'` and `undefined`
                  // cases route to the native execution detail route; a future
                  // second kind added to `RunsListRow` without a case here fails
                  // typecheck via the `never` check in `default`, instead of
                  // silently linking it to the wrong detail route.
                  switch (execution.kind) {
                    case 'external':
                      return buildHref({
                        to: '/runs/external/$runId',
                        params: { runId: execution.id },
                      });
                    case 'cofactor':
                    case undefined:
                      return buildHref({
                        to: '/runs/test/$executionId',
                        params: { executionId: execution.id },
                      });
                    default: {
                      const exhaustiveCheck: never = execution;
                      return exhaustiveCheck;
                    }
                  }
                }}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSortChange={handleSortChange}
                renderNewRunButton={newRunButton}
              />
            </div>
          </div>
        </div>
      </DocsAnnotation>

      <StartTestRunDialog
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        onStart={(options) => {
          setDialogOpen(false);
          if (options.suiteId) {
            runSuiteMutation.mutate({
              suiteId: options.suiteId,
              environmentMode: options.environmentMode,
              reEnableDegraded: options.reEnableDegraded,
            });
          } else {
            startAgenticRunMutation.mutate(options);
          }
        }}
        isPending={startAgenticRunMutation.isPending || runSuiteMutation.isPending}
        activeRunCount={liveExecutions.runs.filter((r) => r.executionStatus === 'running').length}
        promotedWorkflows={runnableWorkflows}
        isPromotedWorkflowPreviewLoading={runnableSummariesQuery.isLoading}
      />

      <TestRunConfirmationDialogs
        cancelTargetId={cancelTargetId}
        deleteTargetId={deleteTargetId}
        isCancelPending={cancelMutation.isPending}
        isDeletePending={deleteMutation.isPending}
        onCancelTargetChange={setCancelTargetId}
        onDeleteTargetChange={setDeleteTargetId}
        onConfirmCancel={handleConfirmCancel}
        onConfirmDelete={handleConfirmDelete}
      />
    </PageLayout>
  );
}
